import React, { useState, useMemo, useRef, useEffect, useCallback } from "react"
import {
    View,
    TouchableOpacity,
    TextInput,
    ActivityIndicator,
    ScrollView,
    Keyboard,
    Platform,
} from "react-native"
import axios from "axios"
import debounce from "lodash.debounce"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { keepInputFocusOnPress } from "@common/utils/keepInputFocusOnPress"

// How long the input waits before closing the dropdown on blur, so a tap on
// an item still registers. Exported for callers that move focus elsewhere
// right after a pick and must wait for this close to have happened first.
export const BLUR_CLOSE_DELAY_MS = 200

/**
 * ChapterSearchDropdown — single-select chapter search.
 *
 * Empty input: shows the in-memory `chapters` prop (already scoped to the
 * parent's class + subject), grouped by parent chapter.
 *
 * Typing (≥2 chars): immediately switches to the global curriculum/search
 * endpoint (subject-scoped, all classes, includes skill_parts, ranked with
 * class proximity) when `onGlobalSelect` is wired. Every result row shows a
 * `{class · subject}` chip; in-scope rows render the chip in a discreet
 * style (faded, regular weight) while out-of-scope rows keep the standard
 * grey badge so the tutor immediately spots cross-class hits. Picking any
 * result fires `onGlobalSelect(result)` — the parent's handler is expected
 * to no-op when the scope already matches.
 *
 * Props:
 * - chapters: Array of { id, name, parent_chapter? }
 * - selectedChapterId: Currently selected chapter ID (string)
 * - onSelectChapter: Callback when chapter is selected (chapterId) => void
 * - loading: Show loading spinner
 * - disabled: Disable input
 * - placeholder: Input placeholder text
 * - label: Optional label above input
 * - priorityChapterIds: Array of chapter IDs to show first (from upcoming assessment)
 * - priorityBadge: Badge text for priority chapters (e.g., "J-5")
 * - allowManualEntry: Show "add manually" option when no results found
 * - onManualChapterName: Callback when manual chapter name is submitted (name) => void
 * - manualChapterName: Currently set manual chapter name (for display in selected chip)
 * - onGlobalSelect: Optional callback fired when picking a result from the
 *   automatic cross-class fallback. Receives `{ chapter_id, name, class,
 *   school_subject_id, school_subject_name, parent_chapter }`.
 * - currentClass: Current class scope (used in the fallback header copy + reset
 *   trigger when it changes).
 * - currentSubjectName: Current subject name (used in the fallback header copy
 *   + reset trigger when it changes).
 * - currentSubjectId: Current subject id — kept as a filter on the global
 *   fallback so the cross-class search stays scoped to the same matière.
 * - aiSuggestions: Optional inline suggestion section rendered at the top of
 *   the open dropdown when the AI image-analysis is not confident enough to
 *   auto-pre-select. Shape: { suggestion?: {id, name, parent_name},
 *   alternatives?: [{id, name, parent_name}] }. Picking an item calls
 *   onSelectChapter normally. Section is hidden once the user types into the
 *   search input so it never clutters the regular search experience.
 * - onNoResultsUseAsInstructions: Optional callback (searchText) => void.
 *   When set, an empty cross-class search shows an option row
 *   "Décrire « … » dans Instructions" instead of a bare "Aucun chapitre
 *   trouvé"; tapping it closes the dropdown and hands the typed text to the
 *   parent, which carries it into Instructions. That is the exact moment the
 *   tutor hits a dead end, where a hint elsewhere on screen is easy to miss.
 */
const ChapterSearchDropdown = ({
    chapters = [],
    selectedChapterId = null,
    onSelectChapter,
    loading = false,
    disabled = false,
    placeholder = "Rechercher un chapitre...",
    label = null,
    priorityChapterIds = [],
    priorityBadge = null,
    allowManualEntry = false,
    onManualChapterName = null,
    manualChapterName = null,
    onGlobalSelect = null,
    currentClass = null,
    currentSubjectName = null,
    currentSubjectId = null,
    aiSuggestions = null,
    onNoResultsUseAsInstructions = null,
}) => {
    const [searchQuery, setSearchQuery] = useState("")
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef(null)

    // Cross-class fallback state.
    const [globalResults, setGlobalResults] = useState([])
    const [globalLoading, setGlobalLoading] = useState(false)
    const lastGlobalQueryRef = useRef("")

    // Get selected chapter for display
    const selectedChapter = useMemo(() => {
        if (!selectedChapterId) return null
        return chapters.find((c) => c.id?.toString() === selectedChapterId?.toString())
    }, [chapters, selectedChapterId])

    // Filter and sort chapters
    const filteredChapters = useMemo(() => {
        let filtered = chapters

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = chapters.filter((chapter) =>
                chapter.name?.toLowerCase().includes(query) ||
                chapter.parent_chapter?.name?.toLowerCase().includes(query)
            )
        }

        // Sort: priority chapters first, then by id ascending so the
        // curriculum-seed insertion order is preserved (chapters were inserted
        // in seed-pedagogical order, so id ASC mirrors the official progression
        // — much more useful to tutors than alphabetical).
        const prioritySet = new Set(priorityChapterIds.map(id => id?.toString()))

        return filtered.sort((a, b) => {
            const aIsPriority = prioritySet.has(a.id?.toString())
            const bIsPriority = prioritySet.has(b.id?.toString())

            if (aIsPriority && !bIsPriority) return -1
            if (!aIsPriority && bIsPriority) return 1

            return (a.id ?? 0) - (b.id ?? 0)
        })
    }, [chapters, searchQuery, priorityChapterIds])

    // Group chapters by parent
    const groupedChapters = useMemo(() => {
        const prioritySet = new Set(priorityChapterIds.map(id => id?.toString()))
        const priorityChapters = []
        const chaptersWithParent = []
        const chaptersWithoutParent = []

        filteredChapters.forEach((chapter) => {
            if (prioritySet.has(chapter.id?.toString())) {
                priorityChapters.push(chapter)
            } else if (chapter.parent_chapter) {
                chaptersWithParent.push(chapter)
            } else {
                chaptersWithoutParent.push(chapter)
            }
        })

        // Group chapters with parent
        const grouped = {}
        chaptersWithParent.forEach((chapter) => {
            const parentId = chapter.parent_chapter.id
            const parentName = chapter.parent_chapter.name
            if (!grouped[parentId]) {
                grouped[parentId] = {
                    id: parentId,
                    name: parentName,
                    chapters: [],
                }
            }
            grouped[parentId].chapters.push(chapter)
        })

        // Grammar-first ordering convention: in any subject where a parent
        // chapter is one of the language-foundation buckets (Grammaire,
        // Conjugaison, Orthographe, Lexique), surface those parents before
        // literature/themes. The sort is a no-op for subjects with no
        // grammar parents (priority is identical for all parents → falls
        // back to the parent_id ASC tiebreak, which mirrors the seed
        // insertion order).
        const GRAMMAR_PARENT_REGEX = /^(grammaire|conjugaison|orthographe|lexique)/i
        const groupedList = Object.values(grouped).sort((a, b) => {
            const aIsGrammar = GRAMMAR_PARENT_REGEX.test(a.name || "")
            const bIsGrammar = GRAMMAR_PARENT_REGEX.test(b.name || "")
            if (aIsGrammar !== bIsGrammar) return aIsGrammar ? -1 : 1
            return Number(a.id) - Number(b.id)
        })

        return { priorityChapters, grouped: groupedList, ungrouped: chaptersWithoutParent }
    }, [filteredChapters, priorityChapterIds])

    // Once the user types 2+ chars, we always defer to the global
    // curriculum/search endpoint (subject-scoped, ranked with class proximity).
    // Local in-memory filtering is reserved for the empty-input view so the
    // user always sees the same ranked results regardless of which class is
    // currently selected.
    const isShowingGlobal =
        !!onGlobalSelect &&
        !loading &&
        searchQuery.trim().length >= 2

    const handleSelect = (chapter) => {
        onSelectChapter?.(chapter.id?.toString())
        setSearchQuery("")
        setIsFocused(false)
        setGlobalResults([])
        Keyboard.dismiss()
    }

    const handleManualSelect = (name) => {
        onManualChapterName?.(name)
        setSearchQuery("")
        setIsFocused(false)
        setGlobalResults([])
        Keyboard.dismiss()
    }

    const handleFocus = () => {
        setIsFocused(true)
        // Clear search on focus to show all options
        setSearchQuery("")
        setGlobalResults([])
    }

    // Tapping the selected chip should reopen the dropdown. The input is
    // unmounted while the chip is visible (no input ref → focus() is a no-op),
    // so we drive isFocused directly and let the effect below focus the
    // newly-mounted input on the next render.
    const openDropdown = () => {
        if (disabled) return
        setSearchQuery("")
        setGlobalResults([])
        setIsFocused(true)
    }

    useEffect(() => {
        if (isFocused) {
            const id = setTimeout(() => inputRef.current?.focus(), 0)
            return () => clearTimeout(id)
        }
    }, [isFocused])

    const handleBlur = () => {
        // Delay to allow click on dropdown item
        setTimeout(() => {
            setIsFocused(false)
        }, BLUR_CLOSE_DELAY_MS)
    }

    const clearSelection = () => {
        onSelectChapter?.(null)
        onManualChapterName?.(null)
        setSearchQuery("")
        setGlobalResults([])
        inputRef.current?.focus()
    }

    // ── Global fallback search ──────────────────────────────────────────────
    // Debounced fetcher. Fires only when `isShowingGlobal` flips on, so we
    // never hit the network when the local result set already covers the
    // user's query.
    const fetchGlobalResults = useCallback((q, subjectId, classContext) => {
        const trimmed = (q || "").trim()
        if (trimmed.length < 2) {
            setGlobalResults([])
            setGlobalLoading(false)
            return
        }
        lastGlobalQueryRef.current = trimmed
        setGlobalLoading(true)
        const params = { q: trimmed, limit: 30 }
        if (subjectId) {
            params.school_subject_id = subjectId
        }
        if (classContext) {
            // Hint the backend to boost candidates near this class without
            // filtering them out — the user is looking for the closest
            // grade equivalent, not a strict same-class match.
            params.class_context = classContext
        }
        axios
            .get("/api/v1/curriculum/search", { params })
            .then((response) => {
                if (lastGlobalQueryRef.current !== trimmed) return
                const rows = response?.data?.data?.results || []
                setGlobalResults(rows)
            })
            .catch(() => {
                if (lastGlobalQueryRef.current !== trimmed) return
                setGlobalResults([])
            })
            .finally(() => {
                if (lastGlobalQueryRef.current !== trimmed) return
                setGlobalLoading(false)
            })
    }, [])

    const debouncedFetchGlobalResults = useMemo(
        () => debounce(fetchGlobalResults, 300),
        [fetchGlobalResults]
    )

    // Auto-fire whenever conditions are met — and tear down the moment the
    // user finds a local match again.
    useEffect(() => {
        if (!isShowingGlobal) {
            setGlobalResults([])
            setGlobalLoading(false)
            return
        }
        // Synchronously flag "loading" so the empty/no-results render branch
        // doesn't flash for ~300ms (debounce window) before the fetch fires.
        // The debounced fetcher will replace this state with the actual
        // result + clear loading, or keep loading until the response lands.
        setGlobalLoading(true)
        debouncedFetchGlobalResults(searchQuery, currentSubjectId, currentClass)
        return () => {
            debouncedFetchGlobalResults.cancel?.()
        }
    }, [isShowingGlobal, searchQuery, currentSubjectId, currentClass, debouncedFetchGlobalResults])

    // Clear cached results when the parent's class/subject scope changes.
    useEffect(() => {
        setGlobalResults([])
        setGlobalLoading(false)
    }, [currentClass, currentSubjectName, currentSubjectId])

    const handleGlobalSelect = (row) => {
        // The endpoint returns rows of either type 'chapter' or 'skill_part';
        // both expose the parent chapter. When the user picks a skill_part
        // row we also forward the part's id + position so the modal can
        // pre-select the matching chip in the SkillPartsChipPicker once the
        // chapter's skill_parts arrive.
        const chapter = row?.chapter
        if (!chapter) return
        const skillPart = row?.type === "skill_part" ? row?.skill_part : null
        onGlobalSelect?.({
            chapter_id: chapter.id,
            name: chapter.name,
            class: chapter.class,
            school_subject_id: chapter.school_subject_id,
            school_subject_name: chapter.school_subject?.name || null,
            parent_chapter: chapter.parent_chapter || null,
            skill_part_id: skillPart?.id ?? null,
            skill_part_position: skillPart?.position ?? null,
        })
        setSearchQuery("")
        setIsFocused(false)
        setGlobalResults([])
        Keyboard.dismiss()
    }

    const hasManualSelection = allowManualEntry && manualChapterName && !selectedChapterId
    const showDropdown = isFocused && !disabled && (chapters.length > 0 || allowManualEntry || searchQuery.trim())
    const prioritySet = new Set(priorityChapterIds.map(id => id?.toString()))

    const renderChapterItem = (chapter) => {
        const isSelected = selectedChapterId?.toString() === chapter.id?.toString()

        return (
            <TouchableOpacity
                key={chapter.id}
                onPress={() => handleSelect(chapter)}
                activeOpacity={0.7}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 12,
                    backgroundColor: isSelected
                        ? theme.colors.primaryLighter
                        : theme.colors.white,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.lighter,
                }}
            >
                <View style={{ flex: 1 }}>
                    <TextM
                        fontSize={14}
                        fontWeight={isSelected ? "SemiBold" : "Regular"}
                        color={theme.colors.dark}
                        numberOfLines={2}
                    >
                        {chapter.name}
                    </TextM>
                    {chapter.parent_chapter && (
                        <TextM
                            fontSize={11}
                            color={theme.colors.grey}
                            style={{ marginTop: 2 }}
                        >
                            {chapter.parent_chapter.name}
                        </TextM>
                    )}
                </View>
                {isSelected && (
                    <FontAwesome5
                        name="check"
                        size={14}
                        color={theme.colors.primary}
                        style={{ marginLeft: 8 }}
                    />
                )}
            </TouchableOpacity>
        )
    }

    const renderGlobalRow = (row, index) => {
        const chapter = row?.chapter
        if (!chapter) return null
        // Defensive safety net: tree_level=1 chapters are abstract parent
        // groupings, never real targets. Backend already filters them out, but
        // we drop them here too in case of regression or stale cache.
        if (chapter.tree_level === 1) return null
        const isSkillPart = row.type === "skill_part"
        const skillPartName = row?.skill_part?.name
        const parentName = chapter.parent_chapter?.name
        const className = chapter.class
        const subjectName = chapter.school_subject?.name
        // The chip is rendered on every row, but with a subtle visual
        // distinction: in-scope rows get a lighter, less prominent chip so
        // the tutor's eye is naturally drawn to out-of-scope rows where the
        // chip carries useful information (which class the chapter is from).
        const isInCurrentScope =
            !!currentClass &&
            !!currentSubjectId &&
            chapter.class === currentClass &&
            Number(chapter.school_subject_id) === Number(currentSubjectId)

        return (
            <TouchableOpacity
                key={`global-${row.type}-${chapter.id}-${row?.skill_part?.id || index}`}
                onPress={() => handleGlobalSelect(row)}
                activeOpacity={0.7}
                style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    padding: 12,
                    backgroundColor: theme.colors.white,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.lighter,
                }}
            >
                <View style={{ flex: 1 }}>
                    {isSkillPart && skillPartName && (
                        <View
                            style={{
                                alignSelf: "flex-start",
                                paddingVertical: 4,
                                paddingHorizontal: 10,
                                borderRadius: 999,
                                borderWidth: 1,
                                borderColor: theme.colors.light,
                                backgroundColor: theme.colors.greyBg,
                                marginBottom: 6,
                                maxWidth: "100%",
                            }}
                        >
                            <TextM
                                fontSize={11}
                                fontWeight="Regular"
                                color={theme.colors.grey}
                                numberOfLines={1}
                            >
                                {skillPartName}
                            </TextM>
                        </View>
                    )}
                    <TextM
                        fontSize={14}
                        fontWeight={isSkillPart ? "Regular" : "SemiBold"}
                        color={theme.colors.dark}
                        numberOfLines={2}
                    >
                        {chapter.name}
                    </TextM>
                    {parentName && (
                        <TextM
                            fontSize={11}
                            color={theme.colors.grey}
                            style={{ marginTop: 2 }}
                            numberOfLines={1}
                        >
                            {parentName}
                        </TextM>
                    )}
                </View>
                {(className || subjectName) && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            // In-scope (current class+matière): bordered chip
                            // in primary blue — pops as the recommended pick.
                            // Out-of-scope: plain grey chip, no icon — neutral
                            // and informative without yelling.
                            backgroundColor: isInCurrentScope
                                ? theme.colors.primaryLighter
                                : theme.colors.lighter,
                            borderWidth: isInCurrentScope ? 1 : 0,
                            borderColor: isInCurrentScope ? theme.colors.primary : "transparent",
                            paddingHorizontal: 8,
                            paddingVertical: 3,
                            borderRadius: 8,
                            marginLeft: 8,
                            marginTop: isSkillPart ? 2 : 0,
                        }}
                    >
                        <TextM
                            fontSize={10}
                            fontWeight="SemiBold"
                            color={isInCurrentScope ? theme.colors.primary : theme.colors.grey}
                        >
                            {[className, subjectName].filter(Boolean).join(" · ")}
                        </TextM>
                    </View>
                )}
            </TouchableOpacity>
        )
    }

    return (
        <View style={{ zIndex: 10000, position: 'relative' }}>
            {label && (
                <TextM
                    color={theme.colors.primary}
                    fontWeight="Medium"
                    fontSize={12}
                    style={{ marginBottom: 5 }}
                >
                    {label}
                </TextM>
            )}

            {/* Selected chapter chip (when not focused) */}
            {(selectedChapter || hasManualSelection) && !isFocused && (
                <TouchableOpacity
                    onPress={openDropdown}
                    activeOpacity={disabled ? 1 : 0.8}
                    accessibilityRole="button"
                    accessibilityLabel={`${selectedChapter ? selectedChapter.name : manualChapterName}, toucher pour changer`}
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: theme.colors.primary,
                        borderRadius: 10,
                        paddingVertical: 12,
                        paddingHorizontal: 12,
                        backgroundColor: theme.colors.primaryLighter,
                    }}
                >
                    <View style={{ flex: 1 }}>
                        <TextM
                            fontSize={14}
                            fontWeight="SemiBold"
                            color={theme.colors.primary}
                            numberOfLines={1}
                        >
                            {selectedChapter ? selectedChapter.name : manualChapterName}
                        </TextM>
                        {selectedChapter?.parent_chapter && (
                            <TextM
                                fontSize={11}
                                color={theme.colors.primary}
                                style={{ marginTop: 2, opacity: 0.7 }}
                            >
                                {selectedChapter.parent_chapter.name}
                            </TextM>
                        )}
                        {hasManualSelection && (
                            <TextM
                                fontSize={11}
                                color={theme.colors.primary}
                                style={{ marginTop: 2, opacity: 0.7 }}
                            >
                                Nouveau chapitre
                            </TextM>
                        )}
                    </View>
                    {selectedChapter && prioritySet.has(selectedChapter.id?.toString()) && priorityBadge && (
                        <View
                            style={{
                                backgroundColor: theme.colors.warning,
                                paddingHorizontal: 8,
                                paddingVertical: 3,
                                borderRadius: 10,
                                marginRight: 8,
                            }}
                        >
                            <TextM
                                fontSize={11}
                                fontWeight="Bold"
                                color={theme.colors.white}
                            >
                                {priorityBadge}
                            </TextM>
                        </View>
                    )}
                    <TouchableOpacity
                        onPress={(e) => {
                            // Stop propagation so the wrapping chip
                            // TouchableOpacity (which refocuses the input
                            // and reopens the dropdown) doesn't fire on web.
                            e?.stopPropagation?.()
                            clearSelection()
                        }}
                        accessibilityRole="button"
                        accessibilityLabel="Désélectionner le chapitre"
                        hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 14,
                            backgroundColor: theme.colors.primary,
                            alignItems: "center",
                            justifyContent: "center",
                            marginLeft: 8,
                        }}
                    >
                        <FontAwesome5
                            name="times"
                            size={14}
                            color={theme.colors.white}
                        />
                    </TouchableOpacity>
                </TouchableOpacity>
            )}

            {/* Search input (shown when focused or no selection) */}
            {((!selectedChapter && !hasManualSelection) || isFocused) && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: isFocused
                            ? theme.colors.primary
                            : theme.colors.lightMore,
                        borderRadius: 10,
                        paddingHorizontal: 12,
                        backgroundColor: disabled
                            ? theme.colors.lighter
                            : theme.colors.white,
                    }}
                >
                    <FontAwesome5
                        name="search"
                        size={14}
                        color={theme.colors.grey}
                    />
                    <TextInput
                        ref={inputRef}
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        placeholderTextColor={theme.colors.grey}
                        editable={!disabled && !loading}
                        style={{
                            flex: 1,
                            paddingVertical: 12,
                            paddingHorizontal: 10,
                            fontSize: 14,
                            fontFamily: "Montserrat",
                            color: theme.colors.dark,
                        }}
                    />
                    {(loading || globalLoading) && (
                        <ActivityIndicator
                            size="small"
                            color={theme.colors.primary}
                        />
                    )}
                </View>
            )}

            {/* Empty-state hint — class+subject scope has 0 chapters in DB.
                Visible before the user types so they know the combo is empty
                and can either type to broaden cross-class or use generation. */}
            {!loading
                && chapters.length === 0
                && !!currentClass
                && !!currentSubjectId
                && !selectedChapter
                && !manualChapterName
                && !searchQuery.trim() && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        backgroundColor: theme.colors.primaryLighter,
                        padding: 10,
                        marginTop: 6,
                        borderRadius: 8,
                    }}
                >
                    <FontAwesome5
                        name="book"
                        size={14}
                        color={theme.colors.primary}
                        style={{ marginRight: 10, marginTop: 1 }}
                    />
                    <View style={{ flex: 1 }}>
                        <TextM
                            fontSize={12}
                            fontWeight="SemiBold"
                            color={theme.colors.primary}
                            wrap
                        >
                            {`Pas encore de chapitres pour ${currentClass} · ${currentSubjectName}`}
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.primary}
                            style={{ marginTop: 2, opacity: 0.7 }}
                            wrap
                        >
                            Tape un mot pour chercher dans d'autres classes, ou tu peux générer directement (Sam créera le chapitre).
                        </TextM>
                    </View>
                </View>
            )}

            {/* Dropdown */}
            {showDropdown && (
                <View
                    {...keepInputFocusOnPress}
                    style={{
                        position: "absolute",
                        top: label ? 75 : 50,
                        left: 0,
                        right: 0,
                        maxHeight: 300,
                        backgroundColor: theme.colors.white,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        overflow: "hidden",
                        ...Platform.select({
                            web: {
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                            },
                            default: {
                                shadowColor: "#000",
                                shadowOffset: { width: 0, height: 4 },
                                shadowOpacity: 0.15,
                                shadowRadius: 12,
                                elevation: 8,
                            },
                        }),
                        zIndex: 10001,
                    }}
                >
                    <ScrollView
                        // "always": taps on non-touchable areas inside the
                        // dropdown (group headers "PROCHAIN CONTRÔLE", parent
                        // chapter labels, padding between sections) don't blur
                        // the input and close the dropdown. With "handled", an
                        // imprecise tap on a section header was killing the
                        // dropdown without registering a choice.
                        keyboardShouldPersistTaps="always"
                        nestedScrollEnabled
                    >
                        {isShowingGlobal ? (
                            <View>
                                {globalLoading && globalResults.length === 0 && (
                                    <View style={{ padding: 20, alignItems: "center" }}>
                                        <ActivityIndicator
                                            size="small"
                                            color={theme.colors.primary}
                                        />
                                    </View>
                                )}
                                {!globalLoading && globalResults.length === 0 && (
                                    onNoResultsUseAsInstructions ? (
                                        /* The dead end of the search becomes an option
                                           row, like "Ajouter « … »" below — the picker
                                           offers the next step instead of a CTA button
                                           shouting over the list. */
                                        <TouchableOpacity
                                            onPress={() => {
                                                const text = searchQuery.trim()
                                                setSearchQuery("")
                                                setIsFocused(false)
                                                setGlobalResults([])
                                                Keyboard.dismiss()
                                                onNoResultsUseAsInstructions(text)
                                            }}
                                            activeOpacity={0.7}
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                padding: 12,
                                                backgroundColor: theme.colors.white,
                                            }}
                                        >
                                            <FontAwesome5
                                                name="pen"
                                                size={13}
                                                color={theme.colors.primary}
                                                style={{ marginRight: 12 }}
                                            />
                                            <View style={{ flex: 1 }}>
                                                <TextM fontSize={14} fontWeight="Medium" color={theme.colors.primary} numberOfLines={1}>
                                                    {`Décrire « ${searchQuery.trim()} » dans Instructions`}
                                                </TextM>
                                                <TextM fontSize={11} color={theme.colors.grey} style={{ marginTop: 2 }}>
                                                    Aucun chapitre trouvé — Sam le créera à partir de ta description
                                                </TextM>
                                            </View>
                                            <FontAwesome5
                                                name="arrow-down"
                                                size={11}
                                                color={theme.colors.grey}
                                                style={{ marginLeft: 8 }}
                                            />
                                        </TouchableOpacity>
                                    ) : (
                                        <View style={{ padding: 20, alignItems: "center" }}>
                                            <TextM
                                                fontSize={13}
                                                color={theme.colors.grey}
                                                style={{ textAlign: "center" }}
                                            >
                                                {`Aucun chapitre trouvé pour "${searchQuery.trim()}"`}
                                            </TextM>
                                        </View>
                                    )
                                )}
                                {globalResults.map((row, idx) =>
                                    renderGlobalRow(row, idx)
                                )}
                            </View>
                        ) : (
                            <>
                                {/* AI suggestions (image flow, medium confidence) — */}
                                {/* rendered only when nothing has been typed yet so */}
                                {/* it never collides with regular search results. */}
                                {(() => {
                                    const hasSearchQuery = !!searchQuery.trim()
                                    const aiSuggestion = aiSuggestions?.suggestion
                                    const aiAlternatives = aiSuggestions?.alternatives || []
                                    const aiItems = [
                                        ...(aiSuggestion ? [aiSuggestion] : []),
                                        ...aiAlternatives,
                                    ]
                                    if (hasSearchQuery || aiItems.length === 0) return null

                                    return (
                                        <View>
                                            <View
                                                style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    backgroundColor: theme.colors.primaryLighter,
                                                    paddingHorizontal: 12,
                                                    paddingVertical: 8,
                                                    borderBottomWidth: 1,
                                                    borderBottomColor: theme.colors.lightMore,
                                                }}
                                            >
                                                <FontAwesome5
                                                    name="magic"
                                                    size={12}
                                                    color={theme.colors.primary}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <TextM
                                                    fontSize={11}
                                                    fontWeight="Bold"
                                                    color={theme.colors.primary}
                                                >
                                                    SUGGÉRÉ PAR SAM
                                                </TextM>
                                            </View>
                                            {aiItems.map((item) => (
                                                <TouchableOpacity
                                                    key={`ai-${item.id}`}
                                                    onPress={() => {
                                                        onSelectChapter?.(item.id?.toString())
                                                        setSearchQuery("")
                                                        setIsFocused(false)
                                                        setGlobalResults([])
                                                        Keyboard.dismiss()
                                                    }}
                                                    activeOpacity={0.7}
                                                    style={{
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        padding: 12,
                                                        backgroundColor: theme.colors.white,
                                                        borderBottomWidth: 1,
                                                        borderBottomColor: theme.colors.lighter,
                                                    }}
                                                >
                                                    <View style={{ flex: 1 }}>
                                                        <TextM
                                                            fontSize={14}
                                                            fontWeight="Regular"
                                                            color={theme.colors.dark}
                                                            numberOfLines={2}
                                                        >
                                                            {item.name}
                                                        </TextM>
                                                        {item.parent_name && (
                                                            <TextM
                                                                fontSize={11}
                                                                color={theme.colors.grey}
                                                                style={{ marginTop: 2 }}
                                                            >
                                                                {item.parent_name}
                                                            </TextM>
                                                        )}
                                                    </View>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    )
                                })()}

                                {/* Priority chapters (from assessment) */}
                                {groupedChapters.priorityChapters.length > 0 && (
                                    <View>
                                        <View
                                            style={{
                                                backgroundColor: theme.colors.warningLight || theme.colors.lighter,
                                                paddingHorizontal: 12,
                                                paddingVertical: 6,
                                                borderBottomWidth: 1,
                                                borderBottomColor: theme.colors.lightMore,
                                                flexDirection: "row",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <TextM
                                                fontSize={11}
                                                fontWeight="Bold"
                                                color={theme.colors.warning}
                                            >
                                                PROCHAIN CONTRÔLE
                                            </TextM>
                                            {priorityBadge && (
                                                <View
                                                    style={{
                                                        backgroundColor: theme.colors.warning,
                                                        paddingHorizontal: 8,
                                                        paddingVertical: 2,
                                                        borderRadius: 10,
                                                    }}
                                                >
                                                    <TextM
                                                        fontSize={10}
                                                        fontWeight="Bold"
                                                        color={theme.colors.white}
                                                    >
                                                        {priorityBadge}
                                                    </TextM>
                                                </View>
                                            )}
                                        </View>
                                        {groupedChapters.priorityChapters.map((chapter) =>
                                            renderChapterItem(chapter)
                                        )}
                                    </View>
                                )}

                                {/* Grouped chapters */}
                                {groupedChapters.grouped.map((group) => (
                                    <View key={group.id}>
                                        <View
                                            style={{
                                                backgroundColor: theme.colors.lighter,
                                                paddingHorizontal: 12,
                                                paddingVertical: 6,
                                                borderBottomWidth: 1,
                                                borderBottomColor: theme.colors.lightMore,
                                            }}
                                        >
                                            <TextM
                                                fontSize={11}
                                                fontWeight="SemiBold"
                                                color={theme.colors.grey}
                                            >
                                                {group.name}
                                            </TextM>
                                        </View>
                                        {group.chapters.map((chapter) =>
                                            renderChapterItem(chapter)
                                        )}
                                    </View>
                                ))}

                                {/* Ungrouped chapters */}
                                {groupedChapters.ungrouped.length > 0 && (
                                    <View>
                                        {groupedChapters.grouped.length > 0 && (
                                            <View
                                                style={{
                                                    backgroundColor: theme.colors.lighter,
                                                    paddingHorizontal: 12,
                                                    paddingVertical: 6,
                                                    borderBottomWidth: 1,
                                                    borderBottomColor: theme.colors.lightMore,
                                                }}
                                            >
                                                <TextM
                                                    fontSize={11}
                                                    fontWeight="SemiBold"
                                                    color={theme.colors.grey}
                                                >
                                                    Autres
                                                </TextM>
                                            </View>
                                        )}
                                        {groupedChapters.ungrouped.map((chapter) =>
                                            renderChapterItem(chapter)
                                        )}
                                    </View>
                                )}

                                {/* Manual entry option at end of results */}
                                {allowManualEntry && searchQuery.trim() && filteredChapters.length > 0 && (
                                    <TouchableOpacity
                                        onPress={() => handleManualSelect(searchQuery.trim())}
                                        activeOpacity={0.7}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            padding: 12,
                                            backgroundColor: theme.colors.lighter,
                                            borderTopWidth: 1,
                                            borderTopColor: theme.colors.lightMore,
                                        }}
                                    >
                                        <FontAwesome5
                                            name="plus-circle"
                                            size={14}
                                            color={theme.colors.primary}
                                            style={{ marginRight: 10 }}
                                        />
                                        <TextM
                                            fontSize={13}
                                            fontWeight="Medium"
                                            color={theme.colors.primary}
                                        >
                                            Ajouter « {searchQuery.trim()} »
                                        </TextM>
                                    </TouchableOpacity>
                                )}

                                {/* Empty state — only reachable when the global branch
                                    is not wired (no onGlobalSelect) or when the user
                                    hasn't typed 2+ chars yet. Once typing starts the
                                    global branch above takes over. */}
                                {filteredChapters.length === 0 && (
                                    <View style={{ padding: allowManualEntry ? 0 : 20, alignItems: "center" }}>
                                        {allowManualEntry && searchQuery.trim() ? (
                                            <TouchableOpacity
                                                onPress={() => handleManualSelect(searchQuery.trim())}
                                                activeOpacity={0.7}
                                                style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    padding: 12,
                                                    backgroundColor: theme.colors.white,
                                                    width: "100%",
                                                }}
                                            >
                                                <FontAwesome5
                                                    name="plus-circle"
                                                    size={14}
                                                    color={theme.colors.primary}
                                                    style={{ marginRight: 10 }}
                                                />
                                                <View style={{ flex: 1 }}>
                                                    <TextM
                                                        fontSize={14}
                                                        fontWeight="Medium"
                                                        color={theme.colors.primary}
                                                    >
                                                        Ajouter « {searchQuery.trim()} »
                                                    </TextM>
                                                    <TextM
                                                        fontSize={11}
                                                        color={theme.colors.grey}
                                                        style={{ marginTop: 2 }}
                                                    >
                                                        Créer un nouveau chapitre
                                                    </TextM>
                                                </View>
                                            </TouchableOpacity>
                                        ) : (
                                            <TextM
                                                fontSize={13}
                                                color={theme.colors.grey}
                                                style={{ textAlign: "center", padding: allowManualEntry ? 12 : 0 }}
                                            >
                                                {searchQuery.trim()
                                                    ? `Aucun chapitre trouvé pour "${searchQuery}"`
                                                    : allowManualEntry
                                                        ? "Tape un nom de chapitre pour le créer"
                                                        : "Aucun chapitre disponible"}
                                            </TextM>
                                        )}
                                    </View>
                                )}
                            </>
                        )}
                    </ScrollView>
                </View>
            )}
        </View>
    )
}

export default ChapterSearchDropdown
